import { useState } from 'react'
import { loadStripe } from '@stripe/stripe-js'

const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || ''
const GENESIS_PRICE_ID = import.meta.env.VITE_STRIPE_PRICE_ID || ''
const TAX_ASSIST_PRICE_ID = import.meta.env.VITE_STRIPE_PRICE_ID_TAX_ASSIST || ''

// Detect pack from current URL
function getPackSlug(): 'genesis' | 'tax-assist' {
  const path = window.location.pathname
  if (path.includes('/tax-assist')) {
    return 'tax-assist'
  }
  return 'genesis' // Default to genesis
}

export function useStripeCheckout() {
  const [isLoading, setIsLoading] = useState(false)

  const handleCheckout = async (couponCode?: string) => {
    if (!STRIPE_PUBLISHABLE_KEY) {
      alert('Stripe configuration is missing. Please set VITE_STRIPE_PUBLISHABLE_KEY environment variable.')
      return
    }

    const packSlug = getPackSlug()
    const priceId = packSlug === 'tax-assist' ? TAX_ASSIST_PRICE_ID : GENESIS_PRICE_ID

    if (!priceId) {
      alert(`Stripe price ID is missing for ${packSlug} pack. Please set the appropriate VITE_STRIPE_PRICE_ID environment variable.`)
      return
    }

    setIsLoading(true)

    try {
      const stripe = await loadStripe(STRIPE_PUBLISHABLE_KEY)
      
      if (!stripe) {
        throw new Error('Failed to load Stripe')
      }

      // Create checkout session via Worker API
      const workerUrl = import.meta.env.VITE_WORKER_URL || 'https://download.harboragent.dev';
      const response = await fetch(`${workerUrl}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId: priceId,
          packSlug: packSlug,
          successUrl: `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/#pricing`,
          couponCode: couponCode || undefined,
        }),
      })

      if (response.ok) {
        const data = await response.json()
        const { sessionId, url } = data
        
        // Use the URL directly if available, otherwise use redirectToCheckout
        if (url) {
          window.location.href = url
        } else if (sessionId) {
        const result = await stripe.redirectToCheckout({ sessionId })
        
        if (result.error) {
          throw new Error(result.error.message)
          }
        } else {
          throw new Error('No session ID or URL returned')
        }
      } else {
        // Backend API not available - provide helpful error message
        const errorText = await response.text().catch(() => 'Unknown error')
        console.error('Backend API error:', errorText)
        throw new Error(
          'Checkout service is currently unavailable. Please contact support@gentlyventures.com to complete your purchase.'
        )
      }
    } catch (error) {
      console.error('Checkout error:', error)
      alert('Unable to start checkout. Please contact support@gentlyventures.com')
    } finally {
      setIsLoading(false)
    }
  }

  return {
    handleCheckout,
    isLoading,
  }
}

